import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { Mail, ArrowLeft, Loader2, Eye, EyeOff, Users } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Turnstile } from "@/components/security/Turnstile";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBotProtection } from "@/hooks/useBotProtection";
import { useTurnstile } from "@/hooks/useTurnstile";
import { generateUUID } from "@/utils/transform";
import { authApi } from "../../services/api";
import { useAuthStore } from "../../store/auth.store";

// Verification Code Input Component
const VerificationCodeInput = ({ value, onChange, disabled }) => {
  const inputRefs = useRef([]);
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);

  useEffect(() => {
    // When value prop changes externally (e.g., cleared), update digits
    if (value === "") {
      setDigits(["", "", "", "", "", ""]);
    }
  }, [value]);

  const handleChange = (index, e) => {
    const val = e.target.value;

    // Only allow digits
    if (val && !/^\d$/.test(val)) return;

    const newDigits = [...digits];
    newDigits[index] = val;
    setDigits(newDigits);
    onChange(newDigits.join(""));

    // Auto-focus next input
    if (val && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newDigits = pastedData
      .split("")
      .concat(["", "", "", "", "", ""])
      .slice(0, 6);
    setDigits(newDigits);
    onChange(newDigits.join(""));

    // Focus last filled input or the one after
    const lastIndex = Math.min(pastedData.length, 5);
    inputRefs.current[lastIndex]?.focus();
  };

  return (
    <div className="flex justify-center gap-2">
      {digits.map((digit, index) => (
        <input
          key={generateUUID()}
          aria-label="Digit"
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-bold border rounded-lg
                     focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                     disabled:opacity-50 disabled:cursor-not-allowed
                     bg-background text-foreground border-input"
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};

export const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authStore = useAuthStore();

  // Get invite token and pre-filled email from URL
  const inviteToken = searchParams.get("invite");
  const prefilledEmail = searchParams.get("email");
  const isInviteRegistration = !!inviteToken;

  // Registration step: 'form' or 'verify'
  const [step, setStep] = useState("form");
  const [registeredEmail, setRegisteredEmail] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: prefilledEmail || "",
    phone: "",
    organisationName: "",
    password: "",
    confirmPassword: "",
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState({});

  // Verification state
  const [verificationCode, setVerificationCode] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Bot protection
  const {
    securityHeaders,
    isReady: securityReady,
    resetFormTimer,
  } = useBotProtection();
  const turnstile = useTurnstile();

  // Reset form timer when component mounts
  useEffect(() => {
    resetFormTimer();
  }, [resetFormTimer]);

  // ============================================
  // TanStack Query - Register Mutation
  // ============================================
  const registerMutation = useMutation({
    mutationFn: async (registrationData) => {
      // Include turnstile token if available
      const data = { ...registrationData };
      if (turnstile.token) {
        data.turnstileToken = turnstile.token;
      }
      return await authApi.register(data, securityHeaders);
    },
    onSuccess: (response) => {
      const data = response.data?.data;

      // Check if tokens were returned (invite-based registration = immediate login)
      if (data?.accessToken) {
        // Try different auth store methods (different stores may have different APIs)
        if (typeof authStore.restoreSession === "function") {
          authStore.restoreSession(data.user, data.accessToken);
        } else if (
          typeof authStore.setUser === "function" &&
          typeof authStore.setAccessToken === "function"
        ) {
          authStore.setAccessToken(data.accessToken);
          authStore.setUser(data.user);
        } else if (typeof authStore.login === "function") {
          // login might need refreshToken, but it's in cookie - try with null
          authStore.login(data.user, data.accessToken, null);
        } else {
          console.error("[Register] No suitable auth store method found");
        }

        toast.success(
          data.message ||
            t("auth.register.welcomeToTeam", "Welcome to the team!"),
        );
        navigate("/dashboard");
        return;
      }

      if (data?.requiresVerification) {
        // Redirect to registration success page
        navigate("/registration-success", {
          state: { email: data.email || formData.email },
        });
      } else {
        // Fallback: old flow (shouldn't happen with new backend)
        toast.success(t("auth.register.registerSuccess"));
        navigate("/login", {
          state: { message: "Account created. Please log in." },
        });
      }
    },
    onError: (error) => {
      const errorData = error.response?.data?.error;

      // Handle challenge required response
      if (errorData?.code === "CHALLENGE_REQUIRED") {
        toast.error(
          t(
            "auth.register.securityCheckRequired",
            "Please complete the security verification",
          ),
        );
        turnstile.reset();
        return;
      }

      const message =
        errorData?.message ||
        error.response?.data?.error ||
        t("auth.register.registerError");
      toast.error(message);
      turnstile.reset();
    },
  });

  // ============================================
  // TanStack Query - Verify Code Mutation
  // ============================================
  const verifyCodeMutation = useMutation({
    mutationFn: async ({ email, code }) =>
      await authApi.verifyRegistrationCode({ email, code }),
    onSuccess: (response) => {
      const data = response.data?.data;

      if (data?.user && data?.tokens) {
        // Auto-login with tokens
        if (typeof authStore.login === "function") {
          authStore.login(
            data.user,
            data.tokens.accessToken,
            data.tokens.refreshToken,
          );
        } else if (typeof authStore.restoreSession === "function") {
          authStore.restoreSession(data.user, data.tokens.accessToken);
        }
        toast.success(t("auth.register.verified"));
        navigate("/dashboard");
      } else {
        toast.success(t("auth.register.verified"));
        navigate("/login", {
          state: { message: "Email verified. Please log in." },
        });
      }
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        t("auth.register.invalidCode");
      toast.error(message);
      setVerificationCode("");
    },
  });

  // ============================================
  // TanStack Query - Resend Code Mutation
  // ============================================
  const resendCodeMutation = useMutation({
    mutationFn: async (email) => await authApi.resendVerification({ email }),
    onSuccess: () => {
      setResendCooldown(60);
      setVerificationCode("");
      toast.success(t("auth.register.codeResent"));
    },
    onError: (error) => {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.error ||
        t("errors.generic");
      toast.error(message);
    },
  });

  const loading = registerMutation.isPending;
  const verifying = verifyCodeMutation.isPending;

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName) {
      newErrors.firstName = t("auth.validation.firstNameRequired");
    }
    if (!formData.lastName) {
      newErrors.lastName = t("auth.validation.lastNameRequired");
    }
    if (!formData.email) {
      newErrors.email = t("auth.validation.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t("auth.validation.emailInvalid");
    }
    if (!formData.password) {
      newErrors.password = t("auth.validation.passwordRequired");
    } else if (formData.password.length < 8) {
      newErrors.password = t("auth.validation.passwordMinLength", { min: 8 });
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = t("auth.validation.passwordRequirements");
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t("auth.validation.passwordsNoMatch");
    }
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = t("auth.validation.termsRequired");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    const registrationData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || undefined,
      organisationName: formData.organisationName || undefined,
      password: formData.password,
      inviteToken: inviteToken || undefined, // Pass invite token if present
      agreeToTerms: formData.agreeToTerms,
    };

    registerMutation.mutate(registrationData);
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();

    if (verificationCode.length !== 6) {
      toast.error(t("auth.register.codeRequired"));
      return;
    }

    verifyCodeMutation.mutate({
      email: registeredEmail,
      code: verificationCode,
    });
  };

  const handleResendCode = () => {
    if (resendCooldown > 0) {
      return;
    }
    resendCodeMutation.mutate(registeredEmail);
  };

  const handleBackToForm = () => {
    setStep("form");
    setVerificationCode("");
    setRegisteredEmail("");
  };

  // Verification Step
  if (step === "verify") {
    return (
      <div>
        <button
          onClick={handleBackToForm}
          className="flex items-center text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t("common.back")}
        </button>

        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {t("auth.register.verifyTitle")}
          </h2>
          <p className="text-muted-foreground">
            {t("auth.register.verifySubtitle", "We've sent a 6-digit code to")}
          </p>
          <p className="font-medium text-foreground">{registeredEmail}</p>
        </div>

        <form onSubmit={handleVerifyCode} className="space-y-6">
          <div className="space-y-2">
            <Label className="text-center block">
              {t("auth.register.enterCode")}
            </Label>
            <VerificationCodeInput
              value={verificationCode}
              onChange={setVerificationCode}
              disabled={verifying}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={verifying || verificationCode.length !== 6}
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("auth.register.verifying")}
              </>
            ) : (
              t("auth.register.verifyButton")
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            {t("auth.register.noCode", "Didn't receive the code?")}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResendCode}
            disabled={resendCooldown > 0}
          >
            {resendCooldown > 0
              ? t("auth.register.resendIn", { seconds: resendCooldown })
              : t("auth.register.resendCode")}
          </Button>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("auth.register.codeExpiry")}
        </p>
      </div>
    );
  }

  // Registration Form Step
  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground text-center mb-2">
        {isInviteRegistration
          ? t("auth.register.joinTeamTitle", "Join Your Team")
          : t("auth.register.title")}
      </h2>
      <p className="text-muted-foreground text-center mb-6">
        {isInviteRegistration
          ? t(
              "auth.register.joinTeamSubtitle",
              "Create your account to join the team",
            )
          : t("auth.register.subtitle")}
      </p>

      {/* Invite registration banner */}
      {isInviteRegistration && (
        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Users className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-200">
              {t(
                "auth.register.inviteNotice",
                "You're joining via team invitation",
              )}
            </p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {t(
                "auth.register.inviteAutoVerify",
                "Your email will be automatically verified and you'll be logged in immediately.",
              )}
            </p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">
              {t("auth.register.firstNameLabel")}
            </Label>
            <Input
              id="firstName"
              name="firstName"
              placeholder={t("auth.register.firstNamePlaceholder")}
              value={formData.firstName}
              onChange={handleChange}
              error={errors.firstName}
            />
            {errors.firstName && (
              <p className="text-sm text-destructive">{errors.firstName}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">{t("auth.register.lastNameLabel")}</Label>
            <Input
              id="lastName"
              name="lastName"
              placeholder={t("auth.register.lastNamePlaceholder")}
              value={formData.lastName}
              onChange={handleChange}
              error={errors.lastName}
            />
            {errors.lastName && (
              <p className="text-sm text-destructive">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.register.emailLabel")}</Label>
          <Input
            id="email"
            type="email"
            name="email"
            placeholder={t("auth.register.emailPlaceholder")}
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            readOnly={!!prefilledEmail}
            className={prefilledEmail ? "bg-muted" : ""}
          />
          {prefilledEmail && (
            <p className="text-xs text-muted-foreground">
              {t(
                "auth.register.emailFromInvite",
                "This email is from your invitation and cannot be changed.",
              )}
            </p>
          )}
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">
            {t("auth.register.phoneLabel")}
            <span className="text-muted-foreground font-normal ml-1">
              ({t("common.optional")})
            </span>
          </Label>
          <Input
            id="phone"
            type="tel"
            name="phone"
            placeholder={t("auth.register.phonePlaceholder")}
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        {/* Hide organisation field for invite registrations - they'll join existing org */}
        {!isInviteRegistration && (
          <div className="space-y-2">
            <Label htmlFor="organisationName">
              {t("auth.register.organisationLabel")}
              <span className="text-muted-foreground font-normal ml-1">
                ({t("common.optional")})
              </span>
            </Label>
            <Input
              id="organisationName"
              name="organisationName"
              placeholder={t("auth.register.organisationPlaceholder")}
              value={formData.organisationName}
              onChange={handleChange}
            />
            <p className="text-xs text-muted-foreground">
              {t(
                "auth.register.organisationHint",
                'Leave blank to use your name (e.g., "John\'s Workspace")',
              )}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="password">{t("auth.register.passwordLabel")}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={t("auth.register.passwordPlaceholder")}
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">
            {t("auth.register.confirmPasswordLabel")}
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              placeholder={t("auth.register.confirmPasswordPlaceholder")}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              className="pr-10"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-destructive">{errors.confirmPassword}</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-start space-x-2">
            <Checkbox
              id="agreeToTerms"
              checked={formData.agreeToTerms}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, agreeToTerms: checked }))
              }
              className="mt-1"
            />
            <Label
              htmlFor="agreeToTerms"
              className="text-sm font-normal leading-relaxed cursor-pointer"
            >
              {t("auth.register.termsAgree")}{" "}
              <Link
                to="/terms"
                target="_blank"
                className="text-primary hover:text-primary/80"
              >
                {t("auth.register.termsOfService")}
              </Link>{" "}
              {t("auth.register.and")}{" "}
              <Link
                to="/privacy"
                target="_blank"
                className="text-primary hover:text-primary/80"
              >
                {t("auth.register.privacyPolicy")}
              </Link>
            </Label>
          </div>
          {errors.agreeToTerms && (
            <p className="text-sm text-destructive">{errors.agreeToTerms}</p>
          )}
        </div>

        {/* Turnstile CAPTCHA - only shows if configured */}
        <Turnstile
          {...turnstile.turnstileProps}
          className="flex justify-center"
        />

        <Button
          type="submit"
          disabled={loading || !securityReady}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.register.creatingAccount")}
            </>
          ) : (
            t("auth.register.createAccount")
          )}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {t("auth.register.haveAccount")}{" "}
        <Link
          to="/login"
          className="text-primary hover:text-primary/80 font-medium"
        >
          {t("auth.register.signIn")}
        </Link>
      </p>
    </div>
  );
};

export default Register;
